import * as THREE from "three";
import { dom } from "../dom";
import {
  COLOR_RULE_SUBSET_PREFIX,
  type ColorRule,
  type ColorRuleMode,
  state,
} from "../state";
import { ifcLoader, scene } from "../viewer/scene";
import { escapeHtmlText, safeCssColor } from "../utils/html";
import { ifcTypeLabel, onLangChange } from "../i18n";

const colorRuleMaterials: Map<string, THREE.MeshLambertMaterial> = new Map();

// Стабильная палитра с хорошим контрастом для тёмной темы.
const PALETTE = [
  "#ef4444",
  "#f97316",
  "#facc15",
  "#22c55e",
  "#10b981",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#a855f7",
  "#ec4899",
  "#84cc16",
  "#eab308",
  "#14b8a6",
  "#8b5cf6",
  "#f43f5e",
];

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function pickColor(key: string, knownIndex: number): string {
  const seed = key ? hashString(key) : knownIndex;
  return PALETTE[seed % PALETTE.length];
}

function valueForMode(mode: ColorRuleMode, expressId: number): string {
  const meta = state.elementMeta.get(expressId);
  if (!meta) {
    return "";
  }
  if (mode === "type") {
    return meta.type || "";
  }
  if (mode === "storey") {
    return meta.storeyName || "(без этажа)";
  }
  if (mode === "mark") {
    return meta.mark || "(без марки)";
  }
  return "";
}

function clearRuleSubsets(): void {
  // ВАЖНО: в Fragments createSubset не создаёт отдельный mesh, а подсвечивает
  // элементы самой модели и возвращает model.object. Поэтому НЕЛЬЗЯ удалять
  // «subset-mesh» из сцены — это удалило бы всю модель (и она не возвращалась
  // даже при сбросе). Достаточно снять подсветку через removeSubset →
  // model.resetHighlight().
  if (!state.ifcModel) {
    state.colorRuleSubsetIds.length = 0;
    return;
  }
  for (const subsetId of state.colorRuleSubsetIds) {
    const material = colorRuleMaterials.get(subsetId);
    if (!material) {
      continue;
    }
    try {
      ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, material, subsetId);
    } catch (_) {
      /* ignore */
    }
  }
  state.colorRuleSubsetIds.length = 0;
}

function disposeRuleMaterials(): void {
  for (const m of colorRuleMaterials.values()) {
    m.dispose();
  }
  colorRuleMaterials.clear();
}

export function clearColorRule(): void {
  clearRuleSubsets();
  disposeRuleMaterials();
  state.activeColorRule = null;
  renderColorRuleLegend();
  updateColorRuleUi();
}

export function applyColorRule(): void {
  if (!state.ifcModel || !state.activeColorRule || state.activeColorRule.mode === "none") {
    clearColorRule();
    return;
  }
  const mode = state.activeColorRule.mode;
  const palette = state.activeColorRule.palette || {};

  const groupIds: Map<string, number[]> = new Map();
  for (const [id] of state.elementMeta) {
    const key = valueForMode(mode, id);
    if (!key) {
      continue;
    }
    let list = groupIds.get(key);
    if (!list) {
      list = [];
      groupIds.set(key, list);
    }
    list.push(id);
  }

  clearRuleSubsets();
  disposeRuleMaterials();
  const sortedKeys = [...groupIds.keys()].sort((a, b) => a.localeCompare(b));
  const newPalette: Record<string, string> = {};
  sortedKeys.forEach((key, idx) => {
    const color = palette[key] || pickColor(key, idx);
    newPalette[key] = color;
    const material = new THREE.MeshLambertMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.95,
      depthTest: true,
    });
    const subsetId = `${COLOR_RULE_SUBSET_PREFIX}${mode}:${key}`;
    colorRuleMaterials.set(subsetId, material);
    const ids = groupIds.get(key) || [];
    if (ids.length === 0) {
      return;
    }
      void ifcLoader.ifcManager
        .createSubset({
          modelID: state.ifcModel.modelID,
          ids,
          material,
          scene,
          removePrevious: true,
          customID: subsetId,
        })
        .then(() => {
          state.colorRuleSubsetIds.push(subsetId);
        })
        .catch(() => {
          /* ignore */
        });
  });
  state.activeColorRule = { mode, palette: newPalette };
  renderColorRuleLegend();
  updateColorRuleUi();
}

export function renderColorRuleLegend(): void {
  if (!state.activeColorRule || state.activeColorRule.mode === "none") {
    dom.colorRuleLegendElement.innerHTML =
      '<p class="muted" style="margin:0; font-size:12px;">Раскраска отключена.</p>';
    return;
  }
  const isTypeMode = state.activeColorRule.mode === "type";
  const entries = Object.entries(state.activeColorRule.palette).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  if (entries.length === 0) {
    dom.colorRuleLegendElement.innerHTML =
      '<p class="muted" style="margin:0; font-size:12px;">Нет данных для раскраски.</p>';
    return;
  }
  dom.colorRuleLegendElement.innerHTML = entries
    .map(([key, color]) => {
      // В режиме «по типу» ключ — это IFC-код; показываем локализованную подпись.
      const label = isTypeMode ? ifcTypeLabel(key) : key;
      return `<div class="color-rule-legend-row"><span class="color-rule-swatch" style="background:${safeCssColor(color)}"></span><span>${escapeHtmlText(label)}</span><span></span></div>`;
    })
    .join("");
}

export function updateColorRuleUi(): void {
  const hasModel = Boolean(state.ifcModel);
  dom.colorRuleModeSelect.disabled = !hasModel;
  dom.colorRuleApplyButton.disabled = !hasModel;
  dom.colorRuleClearButton.disabled = !hasModel || !state.activeColorRule;
}

export function syncColorRuleUi(): void {
  dom.colorRuleModeSelect.value = state.activeColorRule?.mode || "none";
  renderColorRuleLegend();
  updateColorRuleUi();
}

export function initColorRuleControls(): void {
  dom.colorRuleApplyButton.addEventListener("click", () => {
    const mode = (dom.colorRuleModeSelect.value || "none") as ColorRuleMode;
    if (mode === "none") {
      clearColorRule();
      return;
    }
    const next: ColorRule = { mode, palette: {} };
    state.activeColorRule = next;
    applyColorRule();
  });
  dom.colorRuleClearButton.addEventListener("click", () => {
    clearColorRule();
  });
  dom.colorRuleModeSelect.addEventListener("change", () => {
    if (dom.colorRuleModeSelect.value === "none") {
      clearColorRule();
    }
  });
  // При смене языка перерисовываем легенду (подписи типов меняются ru/en).
  onLangChange(() => {
    renderColorRuleLegend();
  });
}
